import {
    useState,
    useEffect,
    useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_ADD_BUILDING, ROUTE_MANAGE_BUILDING_DATA } from '../../routes/Routes';
import GetFetch from '../../hooks/GetFetch';
import PutPostPatchFetch from '../../hooks/PutPostPatchFetch';
import { usStates } from '../../util/StateAbbreviations';
import FormConfig from '../../FormConfig/FormConfig';
import { validateZipCodeString } from '../../util/_helpers';
import { fieldTypeNames, formGroupNames } from '../Shared/shared';
import { showNotification } from '../../util/_helpers';
import '../../sass/styles.scss';


const BuildingsAdd = () => {
    useEffect(() => { getRegions() }, [])
    const { title } = ROUTE_ADD_BUILDING;
    const [isLoading, setIsLoading] = useState(false);
    const [regions, setRegions] = useState([]);

    const statusOptions = [
        'Active',
        'Inactive'
    ];

    const [formData, setFormData] = useState({
        'SequenceNumber': undefined,
        'Name': undefined,
        'Status': statusOptions[0],
        'Facility': undefined,
        'RegionNumber': undefined,
        'Location': {
            'Id': 0,
            'Address1': undefined,
            'Address2': undefined,
            'CityName': undefined,
            'State': undefined,
            'ZipCode': undefined
        },
        'RentUsageFactor': undefined,
        'AdjustedRentSqFt': undefined,
        'AdjustedUsageSqFt': undefined,
        'AnsiRentSqFt': undefined,
        'AnsiUsableSqFt': undefined,
        'JointUseSqFt': undefined,
        'CommunityJointUseSqFt': undefined,
        'LeaseJointUseSqFt': undefined,
        'CongressionalRep': undefined
    });

    const validationGroupRef = useRef(null);
    const navigate = useNavigate();

    const getRegions = async () => {
        const { Success, Message, Errors } = await GetFetch('/v1/Region');
        if (Success) setRegions(Message.map(({ RegionNumber }) => RegionNumber));
        if (Errors.length > 0) console.error(Errors);
    };

    const handleFormSubmission = async () => {
        const fieldsAreUnanimouslyValid = validationGroupRef.current?.instance().validate().isValid;

        if (fieldsAreUnanimouslyValid === false) {
            showNotification(
                'Saving record unsuccessful. One or more required fields are invalid. Please review each field that needs correcting and try again',
                false,
                5000,
                500
            );
            return;
        };

        if (fieldsAreUnanimouslyValid === true) {
            setIsLoading(true);

            try {
                const { Errors, Success } = await PutPostPatchFetch(
                    '/v1/Building/addBuilding',
                    'POST',
                    {
                        'SequenceNumber': formData.SequenceNumber,
                        'Name': formData.Name,
                        'Status': formData.Status,
                        'Facility': formData.Facility,
                        'RegionNumber': formData.RegionNumber,
                        'Location': {
                            'Id': 0,
                            'Address1': formData.Address1,
                            'Address2': formData.Address2,
                            'CityName': formData.CityName,
                            'State': formData.State,
                            'ZipCode': formData.ZipCode
                        },
                        'RentUsageFactor': formData.RentUsageFactor,
                        'AdjustedRentSqFt': formData.AdjustedRentSqFt,
                        'AdjustedUsageSqFt': formData.AdjustedUsageSqFt,
                        'AnsiRentSqFt': formData.AnsiRentSqFt,
                        'AnsiUsableSqFt': formData.AnsiUsableSqFt,
                        'JointUseSqFt': formData.JointUseSqFt,
                        'CommunityJointUseSqFt': formData.CommunityJointUseSqFt,
                        'LeaseJointUseSqFt': formData.LeaseJointUseSqFt,
                        'CongressionalRep': formData.CongressionalRep
                    }
                );
                
                if (Success) {
                    showNotification(
                        `New Building ${formData.SequenceNumber} was created successfully.`,
                        true,
                        4000,
                        500,
                    )
                    navigate(ROUTE_MANAGE_BUILDING_DATA.withSlash);
                };

                if (Errors.length > 0) {
                    showNotification(
                        `Building ${formData.SequenceNumber} was not successful in saving. \nReason: ${save.Errors[0].Message}`,
                        false,
                        5000,
                        500
                    );
                };
                
            } catch (error) {
                showNotification(
                    `Building number ${formData.SequenceNumber} already exists. Please use a different building number.`,
                    false,
                    5000,
                    500
                );
            } finally {
                setIsLoading(false);
            }
        }
    };

    const childProps = {
        buttonConfiguration: [
            {
                buttonType: 'Back',
                label: 'Cancel',
                onClick: () => navigate(ROUTE_MANAGE_BUILDING_DATA.withSlash),
                type: 'default',
                variant: 'outlined'
            },
            {
                buttonType: 'Save',
                label:'Save',
                onClick: handleFormSubmission,
                type: 'default',
                variant: 'contained'
            }
        ],
        formConfiguation: { columnCount: 24 },
        formFieldsConfiguration: [
            {
                colSpan: 4,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.administrative,
                label: 'Building Number',
                maxLength: 6,
                onValueChanged: ({ value }) => setFormData({ ...formData, SequenceNumber: value.toUpperCase() }),
                pattern: /^(?:\d{4}[A-Z]{2}|[A-Z]{2}\d{4})$/,
                patternMessage: 'Building Number format must be AA9999 or 9999AA',
                patternRequired: true,
                requiredRule: true,
                requiredRuleMessage: 'Building Number is required',
                stringLengthMinimum: 6,
                stringLengthMaximum: 6,
                stringLengthMessage: 'Building Number must be exactly six (6) characters',
                stringLengthRequired: true,
                value: formData.SequenceNumber
            },
            {
                colSpan: 4,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.administrative,
                label: 'Name',
                onValueChanged: ({ value }) => setFormData({ ...formData, Name: value }),
                requiredRule: true,
                requiredRuleMessage: 'Name is required',
                value: formData.Name
            },
            {
                colSpan: 3,
                fieldType: fieldTypeNames.selectBox,
                formGroupName: formGroupNames.administrative,
                label: 'Status',
                onValueChanged: ({ value }) => setFormData({ ...formData, Status: value }),
                options: statusOptions,
                value: formData.Status,
            },
            {
                colSpan: 4,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.administrative,
                label: 'Facility',
                maxLength: 4,
                onValueChanged: ({ value }) => setFormData({ ...formData, Facility: value.toUpperCase() }),
                requiredRule: true,
                requiredRuleMessage: 'Facility is required',
                value: formData.Facility
            },
            {
                colSpan: 5,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.address,
                label: 'Address One',
                onValueChanged: ({ value }) => setFormData({ ...formData, Address1: value }),
                requiredRule: true,
                requiredRuleMessage: 'Address One is required',
                value: formData.Address1
            },
            {
                colSpan: 4,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.address,
                label: 'Address Two',
                onValueChanged: ({ value }) => setFormData({ ...formData, Address2: value }),
                value: formData.Address2
            },
            {
                colSpan: 6,
                label: 'City',
                onValueChanged: ({ value }) => setFormData({ ...formData, CityName: value }),
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.address,
                requiredRule: true,
                requiredRuleMessage: 'City name is required',
                value: formData.CityName
            },
            {
                colSpan: 3,
                fieldType: fieldTypeNames.selectBox,
                formGroupName: formGroupNames.address,
                label: 'State',
                onValueChanged: ({ value }) => setFormData({ ...formData, State: value }),
                requiredRule: true,
                requiredRuleMessage: 'State is required',
                options: [...usStates].map((values) => values.value),
                value: formData.State
            },
            {
                colSpan: 3,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.address,
                label: 'Zip Code',
                maxLength: 9,
                onValueChanged: ({ value }) => setFormData({ ...formData, ZipCode: validateZipCodeString(value) }),
                requiredRule: true,
                requiredRuleMessage: 'Zip code is required',
                pattern: /^\d{5}(?:-\d{4})?$/,
                patternMessage: 'Zip Code must be either five (5) numbers or (9) numbers',
                patternRequired: true,
                value: formData.ZipCode
            },
            {
                colSpan: 3,
                fieldType: fieldTypeNames.selectBox,
                formGroupName: formGroupNames.administrative,
                label: 'Region',
                onValueChanged: ({ value }) => setFormData({ ...formData, RegionNumber: value }),
                options: regions,
                requiredRule: true,
                requiredRuleMessage: 'Region is required',
                value: formData.RegionNumber
            },
            {
                colSpan: 5,
                fieldType: fieldTypeNames.textBox,
                formGroupName: formGroupNames.administrative,
                label: 'Congressional Representative',
                onValueChanged: ({ value }) => setFormData({ ...formData, CongressionalRep: value }),
                value: formData.CongressionalRep
            },
            {
                colSpan: 4,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Rent Usage Factor',
                onValueChanged: ({ value }) => setFormData({ ...formData, RentUsageFactor: value }),
                // readOnly: readOnly,
                value: formData.RentUsageFactor
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Adjusted Rent Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, AdjustedRentSqFt: value }),
                value: formData.AdjustedRentSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Adjusted Usage Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, AdjustedUsageSqFt: value }),
                value: formData.AdjustedUsageSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Ansi Rent Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, AnsiRentSqFt: value }),
                value: formData.AnsiRentSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Ansi Usable Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, AnsiUsableSqFt: value }),
                value: formData.AnsiUsableSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Joint Use Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, JointUseSqFt: value }),
                value: formData.JointUseSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Communnity Joint Use Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, CommunityJointUseSqFt: value }),
                value: formData.CommunityJointUseSqFt
            },
            {
                colSpan: 6,
                fieldType: fieldTypeNames.numberBox,
                formGroupName: formGroupNames.costs,
                label: 'Lease Joint Use Sq. Ft.',
                onValueChanged: ({ value }) => setFormData({ ...formData, LeaseJointUseSqFt: value }),
                value: formData.LeaseJointUseSqFt
            }
        ],
        headingTwo: 'Fill out required fields before saving.',
        isLoading,
        loadingMessage: 'Saving...',
        title,
        validationGroupRef: validationGroupRef
    };

    return <FormConfig {...childProps} />;
};

export default BuildingsAdd;